import { Plus } from '@phosphor-icons/react';
import { BodyScrollEvent } from 'ag-grid-community';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AgGridTable, SlidingPanelContent } from 'core/components';
import { Loader } from 'core/elements';
import { ActionButtonType } from 'core/enums';
import { AgGridClientTableProvider } from 'core/states';
import { CorporatePartNotSetUp } from 'corporate/components/CorporatePartNotSetUp';
import { CorporatePartUpsertButton } from 'corporate/components/CorporatePartSetUp';
import { CorporatePartHelper } from 'corporate/helpers';
import { useCanManageParts, useIsCorporatePartEditable } from 'corporate/hooks';
import { ICorporatePart } from 'corporate/types';
import { useGetUomConversions } from 'parts/hooks';
import { Color, Variant } from 'theme/enums';

import { columnDef } from './columns';
import { EditCorporatePartUom } from './EditCorporatePartUom';
import { UomConversionForm } from './UomConversionForm';
import { UomDetails } from './UomDetails';

interface IProps {
  corporatePart?: Partial<ICorporatePart>;
  partNumber: string;
  manufacturerId: number;
  enabled?: boolean;
  fullWidth?: boolean;
  isLoading?: boolean;
  onBodyScroll?: (event: BodyScrollEvent) => void;
}

export const CorporatePartUom = ({
  corporatePart,
  partNumber,
  manufacturerId,
  enabled,
  fullWidth = true,
  isLoading = false,
  onBodyScroll,
}: IProps) => {
  const { t } = useTranslation();

  const [isAddingUomConversion, setIsAddingUomConversion] = useState(false);

  const isSetUp = CorporatePartHelper.isSetUp(corporatePart);
  const isEditable = useIsCorporatePartEditable(corporatePart);
  const canManageParts = useCanManageParts();

  const payload = {
    filters: {
      part: [partNumber],
      manufacturerId: manufacturerId,
    },
  };

  const {
    records,
    isLoading: isDataLoading,
    loadingPercentage,
    currentPage,
  } = useGetUomConversions(payload, {
    enabled: enabled && isSetUp,
    fetchAllPages: true,
  });

  const columnDefs = useMemo(() => columnDef({ canManageParts }), [canManageParts]);

  const defaultColDef = useMemo(
    () => ({
      enableRowGroup: false,
    }),
    []
  );

  const actions = canManageParts
    ? () => [
        {
          type: ActionButtonType.BUTTON,
          label: t('buttonLabelAddUomConversion'),
          startDecorator: <Plus />,
          variant: Variant.SOLID,
          color: Color.SUCCESS,
          onClick: () => setIsAddingUomConversion(true),
        },
      ]
    : undefined;

  return (
    <AgGridClientTableProvider
      tableId="quotePartUom"
      entityType="UomConversion"
      columnDefs={columnDefs}
      defaultColDef={defaultColDef}
      data={records}
      isDataLoading={isDataLoading}
      loadingPercentage={loadingPercentage}
      dataCurrentPage={currentPage}
      hasRowSelection={false}
      onBodyScroll={onBodyScroll}
    >
      <SlidingPanelContent
        title={t('titleUoms')}
        extra={
          !isLoading &&
          isEditable &&
          (isSetUp ? (
            <EditCorporatePartUom corporatePart={corporatePart} />
          ) : (
            <CorporatePartUpsertButton
              corporatePart={corporatePart}
              partNumber={partNumber}
              manufacturerId={manufacturerId}
            />
          ))
        }
        fullWidth={fullWidth}
      >
        <>
          {isLoading && <Loader />}
          {!isLoading && isSetUp && <UomDetails corporatePart={corporatePart} />}
          {!isLoading && !isSetUp && <CorporatePartNotSetUp />}

          {!isLoading && isSetUp && <AgGridTable actions={actions} />}

          <UomConversionForm
            corporatePart={{
              part: partNumber,
              manufacturerId: manufacturerId,
            }}
            isOpen={isAddingUomConversion}
            onClose={() => setIsAddingUomConversion(false)}
          />
        </>
      </SlidingPanelContent>
    </AgGridClientTableProvider>
  );
};
